import React from 'react';
import {
  ChevronLeft, ShieldCheck, FileText, MapPin, Plus, X, Check,
  Droplets, Zap, HomeIcon, Wind, Settings, MessageSquarePlus, ChevronDown, ChevronUp
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { v2, toMoney, INVOICE_TAX_RATE, getServiceName, PANAMA_ZONES, SCHEDULE_TIME_SLOTS, SERVICE_SCOPE } from '../constants';

// ── 服务范围展示组件 ─────────────────────────────────────────
function ServiceScopeCard({ service, lang }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!service) return null;

  const key = Object.keys(SERVICE_SCOPE).find(k =>
    (service.service_name || '').toLowerCase().includes(k) ||
    k.includes((service.service_name || '').toLowerCase().split(' ')[0])
  );
  const scope = key ? SERVICE_SCOPE[key]?.[lang] : null;
  if (!scope) return null;

  const visibleIncludes = expanded ? scope.includes : scope.includes.slice(0, 3);

  return (
    <div style={{ ...v2.card, padding: '14px', marginBottom: '14px', border: '1px solid #BFDBFE', background: '#F8FBFF' }}>
      {/* 头部 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div>
          <p style={{ margin: '0 0 2px 0', fontSize: '10px', color: v2.colors.muted, fontWeight: '800', letterSpacing: '0.3px' }}>
            {lang === 'es' ? 'ALCANCE DEL SERVICIO' : '服务范围说明'}
          </p>
          <b style={{ fontSize: '13px', color: v2.colors.ink }}>
            {lang === 'es' ? '¿Qué incluye este servicio?' : '包含与不包含的内容'}
          </b>
        </div>
        <span style={{ background: '#EFF6FF', color: '#2563EB', fontSize: '11px', fontWeight: '900', padding: '3px 9px', borderRadius: '20px', border: '1px solid #BFDBFE' }}>
          {scope.priceNote}
        </span>
      </div>

      {/* ✅ 包含 */}
      <div style={{ marginBottom: '10px' }}>
        <p style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: '900', color: '#15803D' }}>
          {lang === 'es' ? '✅ Incluido en el servicio' : '✅ 服务包含'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {visibleIncludes.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
              <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#DCFCE7', border: '1px solid #86EFAC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                <Check size={10} color="#16A34A" />
              </div>
              <span style={{ fontSize: '12px', color: '#1E293B', fontWeight: '600', lineHeight: '1.4' }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 展开/收起 */}
      {!expanded && scope.includes.length > 3 && (
        <button onClick={() => setExpanded(true)}
          style={{ background: 'none', border: 'none', color: v2.colors.blue, fontSize: '12px', fontWeight: '800', cursor: 'pointer', padding: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ChevronDown size={14} /> {lang === 'es' ? 'Ver más detalles' : '展开查看更多'}
        </button>
      )}

      {expanded && (
        <>
          {/* ❌ 不包含 */}
          <div style={{ marginBottom: '10px' }}>
            <p style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: '900', color: '#B91C1C' }}>
              {lang === 'es' ? '❌ No incluido (cobro adicional o cotización)' : '❌ 不包含（需额外报价或收费）'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {scope.excludes.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
                  <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                    <X size={10} color="#DC2626" />
                  </div>
                  <span style={{ fontSize: '12px', color: '#1E293B', fontWeight: '600', lineHeight: '1.4' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 📌 规则 */}
          <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px' }}>
            <p style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: '900', color: '#92400E' }}>
              {lang === 'es' ? '📌 Reglas importantes' : '📌 重要规则'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {scope.rules.map((r, i) => (
                <span key={i} style={{ fontSize: '11px', color: '#78350F', fontWeight: '700', lineHeight: '1.4' }}>• {r}</span>
              ))}
            </div>
          </div>

          <button onClick={() => setExpanded(false)}
            style={{ background: 'none', border: 'none', color: v2.colors.muted, fontSize: '12px', fontWeight: '800', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <ChevronUp size={14} /> {lang === 'es' ? 'Cerrar' : '收起'}
          </button>
        </>
      )}
    </div>
  );
}

function getServiceIcon(s) {
  if (s.id === 'S-999') return <MessageSquarePlus size={28} color="#FF9500" />;
  const n = s.service_name.toLowerCase();
  if (n.includes('plom') || n.includes('水')) return <Droplets size={28} color="#2962FF" />;
  if (n.includes('elec') || n.includes('电')) return <Zap size={28} color="#FF9500" />;
  if (n.includes('limp') || n.includes('洁')) return <HomeIcon size={28} color="#34C759" />;
  if (n.includes('aire') || n.includes('空')) return <Wind size={28} color="#00C6FF" />;
  return <Settings size={28} color="#2962FF" />;
}

export default function OrderView() {
  const {
    lang, t, userData, editData, setEditData,
    selectedService, clientPhotos, setClientPhotos, clientMsg, setClientMsg,
    quantity, setQuantity, orderInvoiceRequired, setOrderInvoiceRequired,
    orderInvoiceDetails, setOrderInvoiceDetails,
    promoInput, setPromoInput, appliedPromo, uploading,
    clientZone, setClientZone, orderIsUrgent, setOrderIsUrgent,
    orderScheduleDay, setOrderScheduleDay, orderScheduleDate, setOrderScheduleDate,
    orderScheduleSlot, setOrderScheduleSlot,
    handleApplyPromo, setPreviewImage, setView, compressImage
  } = useAppContext();

  const selectedOrderBase = selectedService ? selectedService.price * quantity : 0;
  const selectedOrderDiscount = appliedPromo ? appliedPromo.discount : 0;
  const selectedOrderServiceSubtotal = toMoney(Math.max(0, selectedOrderBase - selectedOrderDiscount));
  const selectedOrderTaxAmount = orderInvoiceRequired ? toMoney(selectedOrderServiceSubtotal * INVOICE_TAX_RATE) : 0;
  const selectedOrderTotal = toMoney(selectedOrderServiceSubtotal + selectedOrderTaxAmount);
  const hasServiceAddress = Boolean((editData.address || userData?.address || '').trim());
  const invoiceDetailsComplete = !orderInvoiceRequired || Boolean(orderInvoiceDetails.name.trim() && orderInvoiceDetails.taxId.trim() && orderInvoiceDetails.email.trim());
  const canContinueToPayment = clientPhotos.length > 0 && hasServiceAddress && invoiceDetailsComplete && Boolean(clientZone);

  return (
    <div style={{ padding: '14px 16px 24px' }}>
      {/* 页头 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <button onClick={() => setView('home')} style={{ width: '38px', height: '38px', borderRadius: '8px', border: '1px solid #D8DEE8', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <ChevronLeft size={22} />
        </button>
        <div>
          <p style={{ margin: 0, color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>{lang === 'es' ? 'Nuevo pedido' : '新订单'}</p>
          <h2 style={{ fontWeight: '900', margin: 0, color: v2.colors.ink, fontSize: '21px' }}>{getServiceName(selectedService?.service_name, lang)}</h2>
        </div>
      </div>

      {/* 服务概览 */}
      <div style={{ ...v2.card, padding: '14px', marginBottom: '14px', display: 'flex', gap: '12px', alignItems: 'center' }}>
        <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: v2.colors.softBlue, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {selectedService && getServiceIcon(selectedService)}
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: '0 0 4px 0', color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>{lang === 'es' ? 'Servicio seleccionado' : '已选服务'}</p>
          <b style={{ fontSize: '15px', color: v2.colors.ink }}>{getServiceName(selectedService?.service_name, lang)}</b>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: '0 0 4px 0', color: v2.colors.muted, fontSize: '11px', fontWeight: '700' }}>{lang === 'es' ? 'Unidad' : '单价'}</p>
          <b style={{ fontSize: '18px', color: v2.colors.green }}>${selectedService?.price}</b>
        </div>
      </div>

      {/* 服务范围说明 */}
      <ServiceScopeCard service={selectedService} lang={lang} />

      {/* 保障横幅 */}
      <div style={{ background: 'linear-gradient(135deg, #1648D8 0%, #2962FF 55%, #1A3FBB 100%)', padding: '14px', borderRadius: '16px', marginBottom: '14px', color: '#fff', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 16px rgba(41,98,255,0.25)' }}>
        <ShieldCheck size={22} color="#fff" style={{ flexShrink: 0 }} />
        <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.92)', fontWeight: '800', lineHeight: '1.4' }}>{t.warranty_banner}</p>
      </div>

      {/* 地区选择 */}
      <div style={{ ...v2.card, padding: '16px', marginBottom: '14px', border: !clientZone ? `1px solid ${v2.colors.red}` : v2.card.border }}>
        <p style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '900', color: clientZone ? v2.colors.ink : v2.colors.red, display: 'flex', alignItems: 'center', gap: '5px' }}>
          <MapPin size={14} color={clientZone ? '#2962FF' : v2.colors.red} /> {t.zone_label}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {PANAMA_ZONES.map(z => {
            const isSelected = clientZone?.id === z.id;
            const isActive = z.active !== false;
            return (
              <button key={z.id} type="button"
                onClick={() => isActive && setClientZone(isSelected ? null : z)}
                disabled={!isActive}
                style={{
                  padding: '7px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '700',
                  cursor: isActive ? 'pointer' : 'default',
                  border: `1px solid ${isSelected ? '#2962FF' : isActive ? '#D8DEE8' : '#E8EAED'}`,
                  background: isSelected ? '#EBF5FF' : isActive ? '#fff' : '#F4F6F8',
                  color: isSelected ? '#2962FF' : isActive ? v2.colors.ink : '#B0B8C4',
                  opacity: isActive ? 1 : 0.7,
                  display: 'flex', alignItems: 'center', gap: '5px'
                }}>
                {z.label}
                {!isActive && <span style={{ fontSize: '9px', fontWeight: '800', color: '#B0B8C4', backgroundColor: '#E8EAED', borderRadius: '4px', padding: '1px 5px', letterSpacing: '0.3px' }}>
                  {lang === 'es' ? 'PRONTO' : '即将'}
                </span>}
              </button>
            );
          })}
        </div>
        <p style={{ margin: '10px 0 0 0', fontSize: '11px', color: v2.colors.muted }}>
          {lang === 'es' ? '🚀 Fase 1: Centro y Costa del Este. Más zonas próximamente.' : '🚀 第一阶段：中心区 + 东区。更多区域即将开放。'}
        </p>
      </div>

      {/* 预约时间 */}
      <div style={{ ...v2.card, padding: '16px', marginBottom: '14px' }}>
        <p style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '900', color: v2.colors.ink }}>{t.schedule_label}</p>
        {/* 紧急 */}
        <button type="button" onClick={() => setOrderIsUrgent(!orderIsUrgent)}
          style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `1px solid ${orderIsUrgent ? '#EF4444' : '#D8DEE8'}`, background: orderIsUrgent ? '#FEF2F2' : '#fff', color: orderIsUrgent ? '#DC2626' : v2.colors.ink, fontWeight: '800', fontSize: '14px', cursor: 'pointer', marginBottom: '10px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px' }}>
          🚨 {t.schedule_urgent} {orderIsUrgent && ' ✓'}
        </button>
        {!orderIsUrgent && (
          <>
            {/* 日期选择 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '10px' }}>
              {[{ id: 'today', label: t.schedule_today }, { id: 'tomorrow', label: t.schedule_tomorrow }, { id: 'custom', label: lang === 'es' ? 'Otra fecha' : '选日期' }].map(d => (
                <button key={d.id} type="button" onClick={() => setOrderScheduleDay(d.id)}
                  style={{ padding: '10px 6px', borderRadius: '8px', border: `1px solid ${orderScheduleDay === d.id ? '#2962FF' : '#D8DEE8'}`, background: orderScheduleDay === d.id ? '#EBF5FF' : '#fff', color: orderScheduleDay === d.id ? '#2962FF' : v2.colors.ink, fontWeight: '800', fontSize: '13px', cursor: 'pointer' }}>
                  {d.label}
                </button>
              ))}
            </div>
            {orderScheduleDay === 'custom' && (
              <input type="date" value={orderScheduleDate} onChange={e => setOrderScheduleDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={{ ...v2.field, marginBottom: '10px' }} />
            )}
            {/* 时间段 */}
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '800', color: v2.colors.muted }}>{t.schedule_slot_label}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
              {SCHEDULE_TIME_SLOTS.map(s => (
                <button key={s.id} type="button" onClick={() => setOrderScheduleSlot(orderScheduleSlot === s.id ? null : s.id)}
                  style={{ padding: '10px 6px', borderRadius: '8px', border: `1px solid ${orderScheduleSlot === s.id ? '#2962FF' : '#D8DEE8'}`, background: orderScheduleSlot === s.id ? '#EBF5FF' : '#fff', color: orderScheduleSlot === s.id ? '#2962FF' : v2.colors.ink, fontWeight: '800', fontSize: '11px', cursor: 'pointer', lineHeight: '1.4' }}>
                  {lang === 'es' ? s.label_es : s.label_zh}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* 数量 & 价格明细 */}
      <div style={{ ...v2.card, padding: '16px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div>
            <p style={{ margin: '0 0 4px 0', color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>{lang === 'es' ? 'Cantidad' : '数量'}</p>
            <b style={{ fontSize: '15px', color: v2.colors.ink }}>{lang === 'es' ? 'Ajusta si necesitas más unidades' : '按实际需求调整'}</b>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={() => setQuantity(Math.max(1, quantity - 1))} style={{ width: '34px', height: '34px', border: '1px solid #D8DEE8', background: '#fff', borderRadius: '8px', fontSize: '20px', color: v2.colors.red, cursor: 'pointer' }}>-</button>
            <b style={{ fontSize: '20px', width: '28px', textAlign: 'center', color: v2.colors.ink }}>{quantity}</b>
            <button onClick={() => setQuantity(quantity + 1)} style={{ width: '34px', height: '34px', border: '1px solid #D8DEE8', background: '#fff', borderRadius: '8px', fontSize: '20px', color: v2.colors.green, cursor: 'pointer' }}>+</button>
          </div>
        </div>
        <div style={{ borderTop: '1px solid #EEF2F6', paddingTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: v2.colors.muted, marginBottom: '7px' }}>
            <span>Subtotal</span>
            <span style={{ textDecoration: appliedPromo ? 'line-through' : 'none' }}>${selectedOrderBase.toFixed(2)}</span>
          </div>
          {appliedPromo && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#BE123C', marginBottom: '7px' }}>
              <span>Promo {appliedPromo.code}</span><span>-${selectedOrderDiscount.toFixed(2)}</span>
            </div>
          )}
          {appliedPromo && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: v2.colors.muted, marginBottom: '7px' }}>
              <span>{lang === 'es' ? 'Servicio neto' : '服务本金'}</span><span>${selectedOrderServiceSubtotal.toFixed(2)}</span>
            </div>
          )}
          {selectedOrderTaxAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#B45309', marginBottom: '7px' }}>
              <span>ITBMS 7%</span><span>+${selectedOrderTaxAmount.toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <b style={{ fontSize: '15px', color: v2.colors.ink }}>Total</b>
            <b style={{ fontSize: '24px', color: v2.colors.red }}>${selectedOrderTotal.toFixed(2)}</b>
          </div>
        </div>
      </div>

      {/* 优惠码 */}
      <div style={{ ...v2.card, padding: '14px', marginBottom: '14px', display: 'flex', gap: '10px' }}>
        <input placeholder={t.promo_placeholder} value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())} disabled={appliedPromo !== null}
          style={{ ...v2.field, flex: 1, textTransform: 'uppercase', backgroundColor: appliedPromo ? '#F3F4F6' : '#fff' }} />
        <button onClick={handleApplyPromo} disabled={appliedPromo !== null || !promoInput.trim() || uploading}
          style={{ padding: '0 14px', background: appliedPromo ? v2.colors.green : v2.colors.ink, color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: appliedPromo ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
          {appliedPromo ? <Check size={16} /> : t.promo_btn}
        </button>
      </div>

      {/* 发票 */}
      <div style={{ ...v2.card, padding: '16px', marginBottom: '14px', border: orderInvoiceRequired && !invoiceDetailsComplete ? '1px solid #FCD34D' : v2.card.border }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '8px', background: v2.colors.softGold, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FileText size={20} color={v2.colors.gold} />
          </div>
          <div>
            <p style={{ margin: '0 0 4px 0', color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>{lang === 'es' ? 'Factura fiscal' : '正式发票'}</p>
            <b style={{ fontSize: '15px', color: v2.colors.ink }}>{lang === 'es' ? '¿Necesitas factura del técnico?' : '是否需要师傅开发票？'}</b>
            <p style={{ margin: '6px 0 0 0', color: '#854D0E', fontSize: '11px', lineHeight: '1.45', fontWeight: '700' }}>
              {lang === 'es'
                ? 'FixBox emitirá solo un recibo de servicio. La factura fiscal, si aplica, la emite el técnico proveedor.'
                : 'FixBox 只提供服务凭证。如需正式发票，由实际服务师傅按规定开具。'}
            </p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: orderInvoiceRequired ? '12px' : 0 }}>
          <button type="button" onClick={() => setOrderInvoiceRequired(false)}
            style={{ padding: '12px 8px', borderRadius: '8px', border: `1px solid ${!orderInvoiceRequired ? '#BBF7D0' : '#E3E8EF'}`, background: !orderInvoiceRequired ? v2.colors.softGreen : '#fff', color: !orderInvoiceRequired ? '#15803D' : v2.colors.ink, fontWeight: '900', cursor: 'pointer' }}>
            {lang === 'es' ? 'No necesito' : '不需要'}
          </button>
          <button type="button" onClick={() => setOrderInvoiceRequired(true)}
            style={{ padding: '12px 8px', borderRadius: '8px', border: `1px solid ${orderInvoiceRequired ? '#FCD34D' : '#E3E8EF'}`, background: orderInvoiceRequired ? '#FFF7E6' : '#fff', color: orderInvoiceRequired ? '#92400E' : v2.colors.ink, fontWeight: '900', cursor: 'pointer' }}>
            {lang === 'es' ? 'Sí necesito' : '需要发票'}
          </button>
        </div>
        {orderInvoiceRequired && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>{lang === 'es' ? 'Nombre / Razón social' : '发票抬头'}</p>
              <input value={orderInvoiceDetails.name} onChange={e => setOrderInvoiceDetails({ ...orderInvoiceDetails, name: e.target.value })} style={v2.field} />
            </div>
            <div>
              <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>RUC / Cédula</p>
              <input value={orderInvoiceDetails.taxId} onChange={e => setOrderInvoiceDetails({ ...orderInvoiceDetails, taxId: e.target.value })} style={v2.field} />
            </div>
            <div>
              <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>DV</p>
              <input value={orderInvoiceDetails.dv} onChange={e => setOrderInvoiceDetails({ ...orderInvoiceDetails, dv: e.target.value })} style={v2.field} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>{lang === 'es' ? 'Correo para enviar factura' : '接收发票邮箱'}</p>
              <input type="email" value={orderInvoiceDetails.email} onChange={e => setOrderInvoiceDetails({ ...orderInvoiceDetails, email: e.target.value })} style={v2.field} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>{lang === 'es' ? 'Nota para el técnico' : '给师傅的发票备注'}</p>
              <textarea value={orderInvoiceDetails.note} onChange={e => setOrderInvoiceDetails({ ...orderInvoiceDetails, note: e.target.value })} style={{ ...v2.field, height: '68px', resize: 'vertical' }} />
            </div>
          </div>
        )}
      </div>

      {/* 问题照片 */}
      <div style={{ ...v2.card, padding: '16px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div>
            <p style={{ margin: '0 0 4px 0', color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>{lang === 'es' ? 'Evidencia del problema' : '问题照片'}</p>
            <b style={{ fontSize: '15px', color: v2.colors.ink }}>{t.photos_title}</b>
          </div>
          <span style={{ fontSize: '12px', color: clientPhotos.length > 0 ? v2.colors.green : v2.colors.red, fontWeight: '800' }}>{clientPhotos.length}/6</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
          {clientPhotos.map((img, i) => (
            <div key={i} style={{ position: 'relative', height: '82px' }}>
              <img src={img} onClick={() => setPreviewImage(img)} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer' }} alt="p" />
              <X size={14} onClick={e => { e.stopPropagation(); setClientPhotos(clientPhotos.filter((_, idx) => idx !== i)); }}
                style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: '50%', cursor: 'pointer' }} />
            </div>
          ))}
          {clientPhotos.length < 6 && (
            <label style={{ height: '82px', border: '1px dashed #AAB4C2', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC', cursor: 'pointer' }}>
              <Plus color={v2.colors.ink} />
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => compressImage(e.target.files[0], d => setClientPhotos([...clientPhotos, d]))} />
            </label>
          )}
        </div>
        <textarea placeholder={lang === 'es' ? 'Describe el problema...' : '描述问题...'} value={clientMsg} onChange={e => setClientMsg(e.target.value)} style={{ ...v2.field, height: '86px', marginTop: '12px', resize: 'vertical' }} />
      </div>

      {/* 服务地址 */}
      <div style={{ ...v2.card, padding: '16px', marginBottom: '14px', border: hasServiceAddress ? v2.card.border : `1px solid ${v2.colors.red}` }}>
        <p style={{ fontSize: '12px', fontWeight: '800', color: hasServiceAddress ? v2.colors.muted : v2.colors.red, margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <MapPin size={14} /> {lang === 'es' ? 'Dirección del Servicio' : '上门服务详细地址'}
        </p>
        <textarea placeholder={t.addr_placeholder} value={editData.address || ''} onChange={e => setEditData({ ...editData, address: e.target.value })} style={{ ...v2.field, height: '78px', backgroundColor: hasServiceAddress ? '#fff' : v2.colors.softRed, resize: 'vertical' }} />
      </div>

      {/* 继续按钮 */}
      <button onClick={() => setView('payment')} disabled={!canContinueToPayment}
        style={{ width: '100%', padding: '17px', backgroundColor: canContinueToPayment ? v2.colors.ink : '#CBD5E1', color: '#fff', borderRadius: '8px', border: 'none', marginTop: '8px', fontWeight: '900', fontSize: '17px', cursor: canContinueToPayment ? 'pointer' : 'not-allowed', boxShadow: canContinueToPayment ? '0 8px 18px rgba(15,23,42,0.14)' : 'none' }}>
        {!clientZone
          ? (lang === 'es' ? 'Selecciona tu zona' : '请选择服务区域')
          : !clientPhotos.length
            ? (lang === 'es' ? 'Sube al menos 1 foto' : '请先上传至少1张照片')
            : !hasServiceAddress
              ? (lang === 'es' ? 'Completa la dirección' : '请填写服务地址')
              : !invoiceDetailsComplete
                ? (lang === 'es' ? 'Completa datos de factura' : '请填写发票资料')
                : t.btn_order}
      </button>
    </div>
  );
}
